<template>
  <div class="bar-chart-wrapper">
    <div class="queries-count" v-if="totalCount">{{ totalCount.toLocaleString() }}</div>
    <div class="subtitle2 mb-8">{{ totalLabel }}</div>
    <Bar :style="chartStyles" class="bar-chart" :options="chartOptions" :data="chartData" />
  </div>
</template>

<script lang="ts">
import { Bar } from "vue-chartjs";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  BarElement,
  CategoryScale,
  LinearScale,
  type TooltipItem,
  type ChartData,
} from "chart.js";
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { Patterns } from "@coronet/cui";
import { getFormattedDateTime } from "@/_helpers/date";
import defaultsDeep from "lodash/defaultsDeep";

ChartJS.register(Title, Tooltip, BarElement, CategoryScale, LinearScale);

type BarChartDataPoint = { startDate: number | string; endDate: number | string };

export default defineComponent({
  name: "BarChart",
  components: { Bar },
  props: {
    totalCount: {
      type: Number,
      required: false,
    },
    totalLabel: {
      type: String,
      required: false,
    },
    data: {
      type: Array as PropType<unknown[]>,
      required: true,
    },
    barColor: {
      type: String,
      default: "#B1B1E1",
    },
    options: {
      type: Object,
      required: false,
    },
    chartStyles: {
      type: Object,
      default: () => {
        return {
          "max-height": "188px",
        };
      },
    },
  },
  setup(props) {
    const i18n = useI18n();
    const chartOptions = computed(() => {
      const defaultOptions = {
        responsive: true,
        maxBarThickness: 20,
        borderRadius: 10,
        borderSkipped: false,
        backgroundColor: props.barColor,
        scales: {
          x: {
            color: "#E2E2EB",
            ticks: {
              color: "#6A6891",
              font: {
                size: 12,
              },
            },
            grid: {
              display: false,
            },
          },
          y: {
            color: "#E2E2EB",
            ticks: {
              maxTicksLimit: 5,
              precision: 0,
              color: "#6A6891",
              font: {
                size: 12,
              },
            },
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            displayColors: false,
            titleFont: {
              size: 14,
              weight: 400,
              family: "'Source Sans Pro', sans-serif",
            },
            bodyFont: {
              size: 14,
              weight: 400,
              family: "'Source Sans Pro', sans-serif",
            },
            callbacks: {
              title: (tooltipItems: TooltipItem<"bar">[]) => {
                const tooltip = tooltipItems[0];
                const raw = tooltip.raw as BarChartDataPoint;
                const startDate = raw.startDate;
                const endDate = raw.endDate;
                if (Patterns.TIME.test(tooltip.label)) {
                  return (
                    `${getFormattedDateTime(startDate, "LT")} ${i18n.t("general.until")}` +
                    ` ${getFormattedDateTime(endDate, "LT")}` +
                    ` - ${getFormattedDateTime(startDate, "ll")}`
                  );
                }
                if (Patterns.YEAR.test(tooltip.label)) {
                  return `${getFormattedDateTime(startDate, "ll")} - ${getFormattedDateTime(
                    endDate,
                    "ll"
                  )}`;
                }
                if (getFormattedDateTime(startDate, "ll") !== getFormattedDateTime(endDate, "ll")) {
                  return `${getFormattedDateTime(startDate, "ll")} - ${getFormattedDateTime(
                    endDate,
                    "ll"
                  )}`;
                }

                return getFormattedDateTime(startDate, "ll");
              },
              label: (label: TooltipItem<"bar">) => {
                return `${i18n.t("reports.dnsSummaryReport.queries")} ${label.formattedValue}`;
              },
            },
          },
        },
      };

      return defaultsDeep(props.options, defaultOptions);
    });

    return {
      chartData: computed(() => {
        return {
          datasets: [
            {
              data: props.data as ChartData<"bar">["datasets"][number]["data"],
            },
          ],
        };
      }),
      chartOptions,
    };
  },
});
</script>
<style scoped lang="scss">
.bar-chart-wrapper {
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;
  padding: 40px 40px 24px 40px;
}

.queries-count {
  font-family: Quicksand, sans-serif;
  font-size: 48px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.05;
  letter-spacing: -0.6px;
  color: rgb(var(--v-theme-primary));
}
</style>
